// Single-source playback mutex.
//
// The TTS view has three independent ways to make sound:
//   1. `take` — a single chip/take HTMLAudio playback (handleChipPlay,
//      autoplay after handleGenerate).
//   2. `timeline` — Web Audio preview scheduled by useTimelinePreview.
//   3. `pipeline` — the Generate-All or Play-All-Favorites pipelines in
//      TTSView (each plays HTMLAudio per block in sequence).
//   4. `version-bar` — playback of an uploaded version's composite audio from
//      the VersionsBar pills.
//
// Without coordination, starting one of these doesn't stop the others, and
// the user hears them mixed together. Each call site owns a stop function;
// this store registers those functions and enforces "only one active kind
// at a time" by invoking the previous kind's stop when a new one starts.
//
// Why a store and not just direct calls between hooks?
//   - Timeline.tsx and TTSView.tsx don't share a hook tree, so a callback-
//     ref or context would have to live above both. The existing
//     pipelineCoordinator (src/lib/pipelineCoordinator.ts) already solved
//     the cross-tree coordination for two specific channels (preview ↔
//     pipeline); generalising those into a single mutex is the cleanest
//     way to also cover take-playback.
//   - The store stays tiny: just a kind tag + a registration map. No React
//     re-rendering depends on it, so we don't subscribe components — we
//     drive everything via getState()/setState().

import { create } from 'zustand';

export type PlaybackKind = 'take' | 'timeline' | 'pipeline' | 'version-bar';

type StopHandler = () => void;

interface PlaybackSlice {
  /** Which kind of playback is currently producing audio, if any. */
  currentPlayback: PlaybackKind | null;
  /** Per-kind stop handlers. Each call site registers its handler on mount
   *  and unregisters on unmount. Multiple handlers per kind are supported
   *  (e.g. Strict Mode double-mount, future multi-pipeline scenarios) — we
   *  call them all on takeover. */
  stopHandlers: Record<PlaybackKind, Set<StopHandler>>;
}

const initialHandlers = (): Record<PlaybackKind, Set<StopHandler>> => ({
  take: new Set(),
  timeline: new Set(),
  pipeline: new Set(),
  'version-bar': new Set(),
});

export const usePlaybackStore = create<PlaybackSlice>(() => ({
  currentPlayback: null,
  stopHandlers: initialHandlers(),
}));

/** Register a stop handler for the given kind. Returns an unregister
 *  function. Safe to call from useEffect's setup phase. */
export function registerPlaybackStop(
  kind: PlaybackKind,
  fn: StopHandler,
): () => void {
  const set = usePlaybackStore.getState().stopHandlers[kind];
  set.add(fn);
  return () => {
    set.delete(fn);
  };
}

/** Mark `kind` as the active playback. If a different kind was already
 *  active, fire its stop handlers first so it goes silent before the new
 *  audio starts. No-op if the same kind is already active. */
export function startPlayback(kind: PlaybackKind): void {
  const state = usePlaybackStore.getState();
  const prev = state.currentPlayback;
  if (prev === kind) return;
  if (prev && prev !== kind) {
    // Snapshot the set into an array so a handler that calls clearPlayback()
    // (which mutates currentPlayback) doesn't disturb iteration.
    const handlers = Array.from(state.stopHandlers[prev]);
    for (const fn of handlers) {
      try {
        fn();
      } catch {
        /* a stop handler must never block another */
      }
    }
  }
  usePlaybackStore.setState({ currentPlayback: kind });
}

/** Clear the active playback if (and only if) it currently matches `kind`.
 *  This guard prevents a stale "ended"/"pause" event from a take that was
 *  already superseded by a new playback from racing the new kind's
 *  startPlayback() call and clearing it. */
export function clearPlayback(kind: PlaybackKind): void {
  const state = usePlaybackStore.getState();
  if (state.currentPlayback === kind) {
    usePlaybackStore.setState({ currentPlayback: null });
  }
}

/** Fire every registered stop handler across every kind. Use when the
 *  user takes an action that should silence ALL playback regardless of
 *  source (e.g. opening / submitting the Upload modal — same intent as
 *  Next/Prev navigation). Clears `currentPlayback` after so the next
 *  startPlayback() call sees a clean slate. */
export function stopAllPlayback(): void {
  const state = usePlaybackStore.getState();
  for (const kind of Object.keys(state.stopHandlers) as PlaybackKind[]) {
    const handlers = Array.from(state.stopHandlers[kind]);
    for (const fn of handlers) {
      try {
        fn();
      } catch {
        /* a stop handler must never block another */
      }
    }
  }
  if (state.currentPlayback != null) {
    usePlaybackStore.setState({ currentPlayback: null });
  }
}
